import React, { useRef, useState, useEffect } from 'react';
import { Form, Button } from 'react-bootstrap';
import { FiBold, FiItalic, FiUnderline, FiPaperclip, FiX } from 'react-icons/fi';
import './EmailComposer.css';

function EmailComposer({ 
  message, 
  setMessage, 
  attachments = [], 
  setAttachments,
  placeholder = "Type your message here..."
}) {
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const lastMessageRef = useRef('');

  // Sync contentEditable with message prop
  useEffect(() => {
    if (editorRef.current && message !== lastMessageRef.current) {
      // Only update if message changed externally (e.g., reset)
      if (editorRef.current.innerHTML !== message) {
        editorRef.current.innerHTML = message;
      }
      lastMessageRef.current = message;
    }
  }, [message]);

  const formatText = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current.focus();
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const newAttachments = files.map(file => ({
      file,
      name: file.name,
      size: file.size,
      id: Date.now() + Math.random()
    }));
    setAttachments([...attachments, ...newAttachments]);
    e.target.value = ''; // Reset input
  };

  const removeAttachment = (id) => {
    setAttachments(attachments.filter(att => att.id !== id));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="email-composer">
      <div className="composer-toolbar">
        <div className="toolbar-group">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => formatText('bold')}
            title="Bold"
          >
            <FiBold />
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => formatText('italic')}
            title="Italic"
          >
            <FiItalic />
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => formatText('underline')}
            title="Underline"
          >
            <FiUnderline />
          </Button>
        </div>
        <div className="toolbar-group">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            title="Add Attachment"
          >
            <FiPaperclip /> Attach
          </Button>
        </div>
      </div>
      
      <div
        ref={editorRef}
        contentEditable
        className="composer-editor"
        onInput={(e) => {
          const html = e.target.innerHTML;
          lastMessageRef.current = html;
          setMessage(html);
        }}
        data-placeholder={placeholder}
        suppressContentEditableWarning={true}
      />

      {attachments.length > 0 && (
        <div className="composer-attachments">
          {attachments.map((att) => (
            <div key={att.id} className="attachment-item">
              <span className="attachment-name">{att.name}</span>
              <span className="attachment-size">{formatFileSize(att.size)}</span>
              <Button
                variant="link"
                size="sm"
                className="attachment-remove"
                onClick={() => removeAttachment(att.id)}
              >
                <FiX />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default EmailComposer;
